/**
 * Cold-click chat deep-link bridge.
 *
 * A chrome push for a chat message carries `?openChat=<guestId>` in its
 * launchURL. On a cold click OneSignal opens the app root `/`, and the index
 * route's RoleBasedRedirect immediately `navigate('/analytics', { replace })`,
 * dropping the query before any component can read it.
 *
 * So we capture `openChat` once at startup (main.tsx, before the router
 * mounts) into a module variable, then consume it after the authenticated
 * layout mounts (use-onesignal.ts) and open the chat panel via the chat store.
 */
let pendingChatGuestId: string | null = null;

/** Read `openChat` from the current URL and stash it, then strip the param. */
export function capturePendingChat(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const guestId = params.get('openChat');
    if (!guestId) return;

    pendingChatGuestId = guestId;

    // Strip the param so it doesn't linger in the URL or re-trigger.
    params.delete('openChat');
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', url);
  } catch {
    // ignore malformed URL / unavailable history
  }
}

/** Returns the captured guestId once, then clears it. */
export function consumePendingChat(): string | null {
  const guestId = pendingChatGuestId;
  pendingChatGuestId = null;
  return guestId;
}

/**
 * Cold-click mark-as-read bridge.
 *
 * An order / service-reservation chrome push carries `?notificationId=<_id>` in
 * its web_url alongside the deep-link params (`orderId`/`dialog`/`search`). We
 * capture it at startup — before RoleBasedRedirect can strip the query — and
 * consume it after the authenticated layout mounts to mark the matching in-app
 * notification read. Only `notificationId` is stripped; the deep-link params are
 * left intact so the destination page still opens the dialog / runs the search.
 */
let pendingNotificationReadId: string | null = null;

/** Read `notificationId` from the current URL and stash it, then strip the param. */
export function capturePendingNotificationRead(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const notificationId = params.get('notificationId');
    if (!notificationId) return;

    pendingNotificationReadId = notificationId;

    // Strip only this param — deep-link params must survive to the destination.
    params.delete('notificationId');
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', url);
  } catch {
    // ignore malformed URL / unavailable history
  }
}

/** Returns the captured notificationId once, then clears it. */
export function consumePendingNotificationRead(): string | null {
  const notificationId = pendingNotificationReadId;
  pendingNotificationReadId = null;
  return notificationId;
}
